import { Activity, CalendarIcon, CheckCircle2, Cloud, CloudLightning, Download, Globe, HexagonIcon, Layers, Search, Server, Shield, X, XCircle } from "lucide-react";
import { Device } from "../../interfaces/EndpointInterfaces";
import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useToast } from "../../../../hooks/use-toast";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/popover";
import { cn } from "../../../../utils/utils";
import { Calendar } from "../../../ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import OnPremResourceRequests from "../../ResourceRequests/Tabs/OnPremResourceRequests";
import { format } from "date-fns";
import { downloadOnPremRequestReport } from "../../ResourceRequests/Api/resourcerequestapi";
import { myLocalStorage } from "../../../StorageHelper";
import useUserStore from "../../../../services/userStore";

interface ResourceRequestsTabProps {
  device: Device;
}

type PlatformType = "onprem" | "aws" | "azure" | "gcp" | "k8s" | "weburl";

interface PlatformConfig {
  id: PlatformType;
  name: string;
  icon: any;
  color: string;
  description: string;
}

const platformConfigs: PlatformConfig[] = [
  {
    id: "onprem",
    name: "Endpoints",
    icon: Server,
    color: "text-slate-600",
    description: "On-premises infrastructure"
  },
  {
    id: "aws",
    name: "AWS",
    icon: CloudLightning,
    color: "text-orange-600",
    description: "Amazon Web Services"
  },
  {
    id: "azure",
    name: "Azure",
    icon: Cloud,
    color: "text-blue-600",
    description: "Microsoft Azure"
  },
  {
    id: "gcp",
    name: "GCP",
    icon: Layers,
    color: "text-green-600",
    description: "Google Cloud Platform"
  },
  {
    id: "k8s",
    name: "K8s",
    icon: HexagonIcon,
    color: "text-purple-600",
    description: "Kubernetes clusters"
  },
  {
    id: "weburl",
    name: "WebURL",
    icon: Globe,
    color: "text-indigo-600",
    description: "Web applications"
  }
];

const mockRequestCounts = {
  onprem: { all: 8, pending: 0, approved: 0, declined: 0, revoked: 0 },
  aws: { all: 3, pending: 1, approved: 1, declined: 0, revoked: 1 },
  azure: { all: 7, pending: 0, approved: 0, declined: 1, revoked: 0 },
  gcp: { all: 2, pending: 0, approved: 0, declined: 0, revoked: 0 },
  k8s: { all: 5, pending: 1, approved: 0, declined: 0, revoked: 1 },
  weburl: { all: 7, pending: 1, approved: 0, declined: 0, revoked: 1 }
};


export default function ResourceRequestsDeviceDetails({ computerName = "" }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [activePlatform, setActivePlatform] = useState<PlatformType>("onprem");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const { toast } = useToast();
  const latestTenant = myLocalStorage.getItem("latestTenant");
  const tenantName =
    typeof latestTenant === "string"
      ? latestTenant
      : latestTenant?.tenantName || "";
  const { email, role, userRole } = useUserStore((state) => state.user);


  const handleReset = () => {
    setStartDate(undefined)
    setEndDate(undefined)
    setStatusFilter("all")
    setSearchTerm("")
  }

  // Reset selections when filters or platform change
  useEffect(() => {
    setSelectedRequests([]);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, priorityFilter, activePlatform]);

  // When in device context, only show onprem tab

  // Debounce search query for better performance
  const deferredSearchTerm = useDeferredValue(searchTerm);

  // Get current platform config
  const currentPlatformConfig = useMemo(() => {
    return platformConfigs.find(p => p.id === activePlatform) || platformConfigs[0];
  }, [activePlatform]);

  // Get status counts for current platform
  const statusCounts = useMemo(() => {
    return mockRequestCounts[activePlatform] || { all: 0, pending: 0, approved: 0, declined: 0, revoked: 0 };
  }, [activePlatform]);

  const handleSelectRequest = (requestId: string, checked: boolean) => {
    if (checked) {
      setSelectedRequests(prev => [...prev, requestId]);
    } else {
      setSelectedRequests(prev => prev.filter(id => id !== requestId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedRequests(checked ? [] : []); // Will be handled by platform-specific components
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleApprove = (requestId: string) => {
    console.log(`Approving ${activePlatform} request ${requestId}`);
    toast({
      title: "Request Approved",
      description: `${currentPlatformConfig.name} access request has been approved successfully.`,
    });
  };

  const handleDecline = (requestId: string) => {
    console.log(`Declining ${activePlatform} request ${requestId}`);
    toast({
      title: "Request Declined",
      description: `${currentPlatformConfig.name} access request has been declined.`,
    });
  };

  const handleRevoke = (requestId: string) => {
    console.log(`Revoking ${activePlatform} request ${requestId}`);
    toast({
      title: "Access Revoked",
      description: `${currentPlatformConfig.name} access has been revoked successfully.`,
    });
  };

  const handleBulkAction = (action: "approve" | "decline" | "revoke", requestIds: string[]) => {
    console.log(`Bulk ${action} for ${activePlatform} requests:`, requestIds);
    toast({
      title: `Bulk ${action.charAt(0).toUpperCase() + action.slice(1)}`,
      description: `${requestIds.length} ${currentPlatformConfig.name} requests processed successfully.`,
    });
    setSelectedRequests([]);
  };

  const handleDownloadReport = async () => {
    toast({
      title: "Download Started",
      description: `${currentPlatformConfig.name} resource requests report is being downloaded...`,
    });
    try {
      await downloadOnPremRequestReport(
        activePlatform,
        tenantName,
        computerName,
        role,
        startDate ? startDate.toISOString() : "",
        endDate ? endDate.toISOString() : "",
        searchTerm,
        email,
        statusFilter
      );
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Unable to generate the report. Please try again.",
        variant: "destructive",
      });
    }
  };
  const isResetDisabled =
    !searchTerm && !startDate && !endDate && (!statusFilter || statusFilter === "all");

  return (

    <div className="flex flex-col h-full bg-gradient-to-br from-slate-50/50 via-white to-blue-50/30 dark:from-slate-900/50 dark:via-slate-800 dark:to-slate-900/80">
      {/* Header */}
      <div className="p-6 border-b border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm">
        <div className="flex items-center mb-6 flex-end">
          <div></div>

          <div className="flex items-center gap-3">


            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadReport}
              data-testid="button-download-report"
            >
              <Download className="w-4 h-4 mr-1" />
              Download Report
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative max-w-xs">
            <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-slate-400" />
            <Input
              placeholder={`Search requests...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-slate-200 dark:border-slate-600"
              data-testid="input-search"
            />
          </div>

          {/* Start Date Picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-32 justify-start text-left font-normal",
                  !startDate && "text-muted-foreground"
                )}
                data-testid="button-start-date"
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                {startDate ? format(startDate, "MMM dd") : "Start date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => {
                  setStartDate(date);
                  setEndDate(undefined);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          {/* End Date Picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-32 justify-start text-left font-normal",
                  !endDate && "text-muted-foreground"
                )}
                data-testid="button-end-date"
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                {endDate ? format(endDate, "MMM dd") : "End date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={setEndDate}
                initialFocus
                disabled={(date) => {
                  if (!startDate) return true;
                  return date <= startDate
                }}
              />
            </PopoverContent>
          </Popover>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem>
              <SelectItem value="DECLINE">Declined</SelectItem>
              <SelectItem value="Expired">Expired</SelectItem>
              <SelectItem value="Revoked">Revoked</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="destructive"
            onClick={handleReset}
            disabled={isResetDisabled}
            className={cn(
              "justify-start text-left font-normal transition-colors gap-1.5 px-3 py-2",
              isResetDisabled && "opacity-50 cursor-not-allowed"
            )}
            data-testid="button-reset-search"
          >
            <X className="w-2 h-4" />
            Reset
          </Button>
        </div>
      </div>

      {/* Platform Tabs */}


      {/* Platform Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <OnPremResourceRequests
          computerName={computerName}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          startDate={startDate}
          endDate={endDate}
          activeTab="onprem" />

      </div>
    </div>
  )
}